import { useMediaQuery } from '@ltvco/refresh-lib/theme';
import {
  CancellationReasonsModal,
  CancelUserExperienceModal,
  ConfirmCancelModal,
  ContactOptionsModal,
  TrustPilotModal,
} from '@ltvco/refresh-lib/v1';
import { motion } from 'framer-motion';
import { useContext, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import { constants } from 'appConstants';
import {
  BrandLogoChat,
  ChatPageContainer,
  ChatPageSidebarContainer,
  ChatPageMessagesContainer,
  ChatInput,
  ChatPageSidebar,
  ChatPageSidebarMobile,
  ChatMenuStack,
  MenuIcon,
  MessagesContainer,
  ChatPageContainerStack,
} from 'components/ChatBV/components';
import { LOADING_STATUS } from 'components/ChatBV/constants';
import { ChatContext } from 'components/ChatBV/context';
import { useChatForm, useReportState } from 'components/ChatBV/hooks';

import { UpgradePlanChatPage } from '../UpgradePlanPage/UpgradePlanChatPage';

export const ChatPage = () => {
  const { messages, loadingStatus } = useContext(ChatContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { handleSendMessage } = useChatForm(() => {});
  const reportState = useReportState();
  const { closeAllpanels } = reportState;

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);
  const [isReportOpen, setIsReportOpen] = useState(false);
  const [shouldCloseReport, setShouldCloseReport] = useState(false);
  const [showLanding, setShowLanding] = useState(true);
  const [isLogoAnimating, setIsLogoAnimating] = useState(false);
  const [logoAnimationPhase, setLogoAnimationPhase] = useState<
    'center' | 'moving-up' | 'moving-left' | 'header'
  >('center');
  const [hasCompletedAnimation, setHasCompletedAnimation] = useState(false);
  const [isContactOptionsModalOpen, setIsContactOptionsModalOpen] =
    useState(false);
  const [isConfirmCancelModalOpen, setIsConfirmCancelModalOpen] =
    useState(false);
  const [showCheaperPlanLink, setShowCheaperPlanLink] = useState(false);
  const [isCancellationReasonsModalOpen, setIsCancellationReasonsModalOpen] =
    useState(false);
  const [, setIsSuccessModaVisible] = useState<boolean>(false);
  const [, setIsDownsellOfferModalOpen] = useState(false);
  const [isUserExperienceSurveyModalOpen, setIsUserExperienceSurveyModalOpen] =
    useState(false);
  const [isTrustPilotModalOpen, setIsTrustPilotModalOpen] = useState(false);

  // Check if we're on the library route
  const isRecentReportsOpen = location.pathname === '/chatbv/chat/library';
  const isUpgradePlanOpen = location.pathname === '/chatbv/chat/upgrade/plan';

  // Use MUI's useMediaQuery with custom breakpoint to target exactly 1024px
  const isMobileBreakpoint = useMediaQuery('(max-width: 1024px)');

  useEffect(() => {
    document.body.classList.add('chat-page');

    return () => {
      document.body.classList.remove('chat-page');
    };
  }, []);

  const headerLogoVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: 0.3 },
    },
  };

  // Handle transition from landing page to chat
  useEffect(() => {
    // Only trigger animation if there are messages AND we're on landing page
    // AND the last message is from the user (not assistant)
    if (messages.length > 0 && showLanding) {
      const lastMessage = messages[messages.length - 1];
      const isUserMessage = lastMessage?.role === 'user';

      if (isUserMessage) {
        // Start logo animation
        setIsLogoAnimating(true);
        setLogoAnimationPhase('moving-up');

        // After logo moves up, move it to the left
        setTimeout(() => {
          setLogoAnimationPhase('moving-left');
        }, 800);

        // After logo moves left, transition to chat
        setTimeout(() => {
          setLogoAnimationPhase('header');
          setTimeout(() => {
            setHasCompletedAnimation(true);
            setShowLanding(false);
            setIsLogoAnimating(false);
          }, 0);
        }, 1200);
      }
    } else if (messages.length === 0 && !showLanding) {
      setShowLanding(true);
      setLogoAnimationPhase('center');
      setHasCompletedAnimation(false);
    }
  }, [messages.length, showLanding, messages]);

  const handleMenuClick = () => {
    if (isSidebarOpen) {
      setIsAnimatingOut(true);
      setTimeout(() => {
        setIsSidebarOpen(false);
        setIsAnimatingOut(false);
      }, 300);
    } else {
      setIsSidebarOpen(true);
    }
  };

  const handleUpgradePlanClick = () => {
    navigate('/chatbv/chat/upgrade/plan');
    if (isSidebarOpen) {
      setIsAnimatingOut(true);
      setTimeout(() => {
        setIsSidebarOpen(false);
        setIsAnimatingOut(false);
      }, 300);
    }
  };

  const handleCloseUpgradePlan = () => {
    navigate('/chatbv/chat');
  };

  const handleRecentReportsClick = () => {
    navigate('/chatbv/chat/library');
    // Close mobile sidebar when opening recent reports
    closeAllpanels();
    if (isSidebarOpen) {
      setIsAnimatingOut(true);
      setTimeout(() => {
        setIsSidebarOpen(false);
        setIsAnimatingOut(false);
      }, 300);
    }
  };

  const handleCloseRecentReports = () => {
    navigate('/chatbv/chat');
    closeAllpanels();
    setShouldCloseReport(true);
  };

  const contactUsHandler = () => {
    setIsContactOptionsModalOpen(true);
  };

  const openConfirmCancelModal = (downsellTest?: boolean) => {
    setIsConfirmCancelModalOpen(true);
    setShowCheaperPlanLink(downsellTest || false);
  };

  const openSuccessModal = () => {
    setIsSuccessModaVisible(true);
  };

  const handleCancellationModalClose = (reason: string | undefined) => {
    setIsCancellationReasonsModalOpen(false);
    if (reason === 'Too expensive') {
      setIsDownsellOfferModalOpen(true);
    } else if (reason === 'Other' || reason === 'No longer needed') {
      setIsUserExperienceSurveyModalOpen(true);
    }
  };

  const handleCloseUserExperienceSurveyModal = (
    rating: string | null | undefined
  ) => {
    setIsUserExperienceSurveyModalOpen(false);
    if (rating && parseInt(rating) >= 8) {
      setIsTrustPilotModalOpen(true);
    }
  };

  return (
    <ChatPageContainer>
      {!isMobileBreakpoint ? (
        <ChatPageSidebarContainer>
          <ChatPageSidebar
            onRecentReportsClick={handleRecentReportsClick}
            onCloseRecentReports={handleCloseRecentReports}
            onUpgradePlanClick={handleUpgradePlanClick}
          />
        </ChatPageSidebarContainer>
      ) : (
        <>
          {!(showLanding && isMobileBreakpoint) && !isReportOpen && (
            <ChatMenuStack>
              <motion.div
                variants={headerLogoVariants}
                initial="hidden"
                animate={hasCompletedAnimation ? 'visible' : 'hidden'}
              >
                <BrandLogoChat
                  width="112px"
                  height="18px"
                  sx={{
                    transform: 'translateY(0) translateX(0) scale(0.7)',
                  }}
                />
              </motion.div>
              <MenuIcon onClick={handleMenuClick} />
            </ChatMenuStack>
          )}
          {(isSidebarOpen || isAnimatingOut) && (
            <ChatPageSidebarMobile
              isOpen={isSidebarOpen && !isAnimatingOut}
              setIsOpen={setIsSidebarOpen}
              onRecentReportsClick={handleRecentReportsClick}
              onCloseRecentReports={handleCloseRecentReports}
              onUpgradePlanClick={handleUpgradePlanClick}
            />
          )}
        </>
      )}
      <ChatPageMessagesContainer>
        {isUpgradePlanOpen ? (
          <ChatPageContainerStack>
            <UpgradePlanChatPage
              contactUsHandler={contactUsHandler}
              onCloseUpgradePlan={handleCloseUpgradePlan}
            />
          </ChatPageContainerStack>
        ) : (
          <MessagesContainer
            reportState={reportState}
            messages={messages}
            loadingStatus={loadingStatus}
            onReportStateChange={setIsReportOpen}
            shouldCloseReport={shouldCloseReport}
            onReportCloseHandled={() => setShouldCloseReport(false)}
            showLanding={showLanding}
            isLogoAnimating={isLogoAnimating}
            logoAnimationPhase={logoAnimationPhase}
            onSendMessage={handleSendMessage}
            sx={{
              ...(isMobileBreakpoint &&
                !isReportOpen && {
                  paddingTop: '0',
                }),
            }}
          />
        )}
      </ChatPageMessagesContainer>
      {!isRecentReportsOpen && messages.length > 0 && !showLanding && (
        <>
          {isMobileBreakpoint && (
            <ChatInput
              isReportOpen={isReportOpen}
              isLanding={false}
              isAnimating={isLogoAnimating}
              animationPhase={logoAnimationPhase}
              isLoading={loadingStatus !== LOADING_STATUS.IDLE}
            />
          )}
        </>
      )}
      <ContactOptionsModal
        isOpen={isContactOptionsModalOpen}
        setIsOpen={setIsContactOptionsModalOpen}
        openConfirmCancelModal={openConfirmCancelModal}
        plansWithout5ReportBlocker={constants.data.plansWithout5ReportBlocker}
        validReportCountThreshold={constants.data.validReportCountThreshold}
        blockUpgradeWindowThreshold={constants.data.blockUpgradeWindowThreshold}
      />
      <ConfirmCancelModal
        isOpen={isConfirmCancelModalOpen}
        onClose={() => setIsConfirmCancelModalOpen(false)}
        onCancel={() => {
          setIsConfirmCancelModalOpen(false);
          setIsCancellationReasonsModalOpen(true);
        }}
        openSuccessModal={openSuccessModal}
        showDownsell={true}
        showCheaperPlanLink={showCheaperPlanLink}
      />

      <CancellationReasonsModal
        isOpen={isCancellationReasonsModalOpen}
        onClose={(reason) => {
          handleCancellationModalClose(reason);
        }}
      />
      {isUserExperienceSurveyModalOpen && (
        <CancelUserExperienceModal
          onClose={(rating) => handleCloseUserExperienceSurveyModal(rating)}
        />
      )}
      <TrustPilotModal
        isOpen={isTrustPilotModalOpen}
        onClose={() => setIsTrustPilotModalOpen(false)}
      />
    </ChatPageContainer>
  );
};
